<template>
  <Modal name="CREATE_EDIT_QUESTION_MODAL" actions :showHeader="false" :width="640">
    <template v-slot:body>
      <h-loading v-show="loading" />

      <div v-show="!loading">
        <v-form v-if="question" v-model="valid">
          <QuestionTypeSelect ref="typeSelect" v-model="questionType" @resetAnswers="deleteAnswers" :answers="answers" />

          <div class="d-flex">
            <v-text-field
              v-model="questionLabel"
              :label="$t('question.create.label')"
              :rules="[rules.required]"
              hide-details="auto"
              outlined
            />
            <v-text-field
              v-if="showMinSizeInput"
              v-model="minSize"
              :label="$t('question.create.minSize')"
              :rules="[rules.cantBeNegative]"
              hide-details="auto"
              class="ml-5 mw-150"
              type="number"
              outlined
            />
          </div>

          <div class="d-flex mt-2">
            <HCheckbox v-model="questionRequired" :inputValue="question.required" :label="$t('question.create.required')" class="mr-5" />
            <HCheckbox v-show="showNoAnswerAlertButton" v-model="createTextAnswerAlert" :inputValue="createTextAnswerAlert" :label="$t('question.create.createAlert')" />
          </div>

          <div v-for="(answer, index) in answers" :key="index" class="mt-5">
            <div class="d-flex align-center">
              <v-text-field v-if="questionType == 'ENUM'" v-model="answer.string_value" :label="$t('question.create.answer')" :rules="[rules.required]" outlined />
              <v-text-field v-if="questionType == 'NUMERIC'" v-model="answer.numeric_value" :label="$t('question.create.answer')" :rules="[rules.required, rules.amount]" class="amount amount--small inputAmount mw-200" type="number" outlined />
              <v-text-field v-if="questionType == 'BOOLEAN'" v-model="answer.label" disabled :label="$t('question.create.answer')" outlined />

              <v-icon @click="() => removeAnswer(answer, index)" class="mt-n7" color="red lighten-2" right>
                mdi-close-circle-outline
              </v-icon>
            </div>
            <HCheckbox v-model="answer.createAlert" :inputValue="answer.createAlert" :label="$t('question.create.createAlert')" class="mt-n6" />
          </div>

          <div class="mt-5 text-right" v-if="questionType && (questionType == 'NUMERIC' || questionType == 'ENUM')">
            <v-btn @click="addAnswer()" color="primary" outlined>
              <v-icon left>mdi-plus-circle-outline</v-icon>
              <span class="body-2 font-weight-medium">
                {{ $t('question.create.actions.addAnswer') }}
              </span>
            </v-btn>
          </div>
        </v-form>
      </div>
      <v-divider class="mt-5" />
    </template>

    <template v-slot:actions>
      <v-btn
        @click="callCloseModal()"
        :disabled="isSaving"
        color="grey"
        class="mr-5"
        large
        text
      >
        {{ $t('question.create.cancel') }}
      </v-btn>
      <v-btn
        @click="isCreating ? create() : update()"
        :disabled="disableCreateButton"
        :loading="isSaving"
        elevation="0"
        color="primary"
        large
      >
        {{ $t('question.create.createButton') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, amount, cantBeNegative } from '@/util/errorHandling'

import Modal from '@/components/app/Modal'
import HCheckbox from '@/components/app/HCheckbox'
import QuestionTypeSelect from '@/components/questionnaire/QuestionTypeSelect'

export default {
  name: 'QuestionModal',

  components: {
    Modal,
    HCheckbox,
    QuestionTypeSelect
  },

  data() {
    return {
      answers: [],
      answersToDelete: [],
      questionType: null,
      questionnaireId: null,
      createTextAnswerAlert: false,
      rules: {
        cantBeNegative,
        required,
        amount,
      },
      valid: false,
      loading: false,
      isCreating: false,
      isSaving: false,
      questionLabel: '',
      questionRequired: false,
      minSize: 120,
    }
  },

  computed: {
    ...mapGetters(['question']),

    disableCreateButton() {
      if (!this.valid) return true
      if (this.answers.length <= 0 && !(this.questionType == 'BOOLEAN' || this.questionType == 'TEXT' || this.questionType == 'FILE')) return true
      return false
    },

    showNoAnswerAlertButton() {
      return this.questionType == 'TEXT' || this.questionType == 'FILE'
    },

    showMinSizeInput() {
      return this.questionType == 'TEXT'
    },
  },

  watch: {
    question(value) {
      if (!value) {
        this.questionLabel = ''
        this.questionRequired = false
      } else {
        this.questionLabel = value.label
        this.questionRequired = value.required
      }
    },

    questionType(newVal, oldVal) {
      if (newVal == "BOOLEAN") {
        this.answers.push({ label: 'Sim', bool_value: true, createAlert: false })
        this.answers.push({ label: 'Não', bool_value: false, createAlert: false })
      }
    },
  },

  methods: {
    ...mapActions([
      'closeModal',
      'openModal',
      'createQuestion',
      'editQuestion',
      'updateAnswer',
      'showErrorMessage',
      'showSuccessMessage',
      'fetchQuestion',
      'fetchQuestionnaireQuestions',
      'createQuestionAlert',
      'deleteAlertsFromQuestion'
    ]),

    openCreateQuestionModal(id, ordinance) {
      this.isCreating = true
      this.questionLabel = ''
      this.openModal('CREATE_EDIT_QUESTION_MODAL')

      this.resetForm()
      this.questionnaireId = id
      this.question.ordinance = ordinance
    },

    async openUpdateQuestionModal(id, questionnaireId) {
      this.loading = true
      this.isCreating = false
      this.openModal('CREATE_EDIT_QUESTION_MODAL')

      await this.fetchQuestion(id).finally(() => this.loading = false)

      this.questionnaireId = questionnaireId ?? null
      this.questionType = this.question.type
      this.answersToDelete = []
      this.createTextAnswerAlert = false
      this.answers = this.question.answer_options
      this.minSize = this.question.min_size

      if (this.answers?.length >= 1) {
        for (var index = 0; index < this.answers.length; index++) {
          this.answers[index].createAlert = this.answers[index].alert != null
        }
      }

      if (this.question.alerts.length > 0 && (this.questionType == 'TEXT' || this.questionType == 'FILE')) {
        this.createTextAnswerAlert = true;
      }
    },

    create() {
      this.isSaving = true
      const currentQuestionnaireId = this.$route.params.id

      this.createQuestion({questionnaireId: this.questionnaireId, params: this.buildQuestionBody()})
        .then(({ data }) => {
          if (data?.id) {
            const answers = this.buildAnswers()
            const answerParams = {
              questionId: data.id,
              params: {
                options_request: answers,
                delete_options_request: []
              }
            }

            this.updateAnswer(answerParams)
              .catch(({ data }) => {
                this.showErrorMessage(data.message)
                return
              })
              .finally(() => {
                currentQuestionnaireId && this.fetchQuestionnaireQuestions(currentQuestionnaireId)
                this.showSuccessMessage(this.$t('question.create.successMessage'))
                this.callCloseModal()
              })

            if (this.createAnswerAlert && (this.questionType == 'TEXT' || this.questionType == 'FILE')) {
              this.createQuestionAlert({questionId: questionId})
            }
          }
        })
        .catch(({ data }) => this.showErrorMessage(data.message))
        .finally(() => { this.isSaving = false })
    },

    update() {
      this.isSaving = true
      const currentQuestionnaireId = this.$route.params?.id

      if (this.deleteAlerts()) this.deleteAlertsFromQuestion(this.question.id)

      this.editQuestion(this.buildQuestionBody())
        .then(({ data }) => {
          const answers = this.buildAnswers()

          if (this.question.type !== 'FILE' && this.question.type !== 'TEXT') {
            this.updateAnswer({
              questionId: data.id,
              params: {
                options_request: answers,
                delete_options_request: this.answersToDelete
              }
            })
              .catch(({ data }) => {
                this.showErrorMessage(data.message)
                return
              })
          }

          if (this.createTextAnswerAlert && (this.question.type == 'TEXT' || this.question.type == 'FILE')) {
            this.createQuestionAlert({ question_id: data.id })
          }
        })
        .catch(({ data }) => this.showErrorMessage(data.message))
        .finally(() => {
          currentQuestionnaireId && this.fetchQuestionnaireQuestions(currentQuestionnaireId)
          this.showSuccessMessage(this.$t('question.edit.successMessage'))
          this.callCloseModal()

          this.isSaving = false
        })
    },

    deleteAlerts() {
      if (this.question.alerts?.filter(alert => alert.answer_option_id == null).length > 0 && this.question.type != 'TEXT') {
        return true
      }
      if (!this.createTextAnswerAlert && this.question.type == 'TEXT') {
        return true
      }

      return false
    },

    buildAnswers() {
      let buildedAnswers = []

      for (let index = 0; index < this.answers.length; index++) {
        const answer = this.answers[index];
        const id = answer.id ? answer.id : null
        const label = this.getAnswerLabel(answer)
        const numVal = answer.numeric_value ? answer.numeric_value : '';
        const stringVal = answer.string_value ? answer.string_value : '';
        const bool_value = this.questionType == "BOOLEAN" ? answer.bool_value : '';
        const buildedAnswer = this.buildAnswer(label, index + 1, bool_value, numVal, stringVal, id, answer.createAlert)
        buildedAnswers.push(this.removeNullProperties(buildedAnswer))
      }

      return buildedAnswers
    },

    getAnswerLabel(answer) {
      switch (this.questionType) {
        case "ENUM":
          return answer.string_value
        case "NUMERIC":
          return answer.numeric_value
        case "BOOLEAN":
          return answer.label
        default:
          return ''
      }
    },

    buildQuestionBody() {
      return {
        id: this.question.id,
        label: this.questionLabel,
        required: this.questionRequired,
        ordinance: this.question.ordinance,
        type: this.questionType,
        min_size: this.minSize,
      }
    },

    buildAnswer(label, ordinance, boolVal, numVal, stringVal, id, createAlert) {
      return {
        id: id ?? null,
        label: label,
        ordinance: ordinance,
        type: this.questionType,
        bool_value: boolVal,
        numeric_value: numVal,
        string_value: stringVal,
        create_alert: createAlert
      }
    },

    addAnswer() {
      this.answers.push({ value: null })
    },

    deleteAnswers() {
      var answersIds = this.answers?.filter((answer) => answer.id).map((answer) => answer.id)
      for (var index = 0; index < answersIds.length; index++) {
        this.answersToDelete.push(answersIds[index])
      }
      this.answers = []
    },

    callCloseModal() {
      this.resetForm()
      this.closeModal('CREATE_EDIT_QUESTION_MODAL')
    },

    resetForm() {
      this.questionnaireId = null
      this.questionType = null
      this.answers = []
      this.minSize = 120
    },

    removeAnswer(answer, index) {
      if (answer.id) this.answersToDelete.push(answer.id)
      this.answers.splice(index, 1)
    },

    removeNullProperties(obj) {
      for (const key in obj) {
        if (obj[key] === null) {
          delete obj[key]
        }
      }

      return obj
    },
  },
}
</script>
